import { Building2 } from "lucide-react";

import { CollapsibleCard } from "@/components/ui/collapsible-card";
import { Input } from "@/components/ui/input";
import { BadgeStatus } from "@/components/ui/badge-status";
import { sprancaOptions, useOfficeStore } from "./useOfficeStore";
import { SprancaUploadPanel } from "./SprancaUploadPanel";

/**
 * P3 — global office identity setup. The architect enters their letterhead
 * once (name / tagline / contact); it applies to every project's izjave footer
 * (the legal wording stays standard). Persisted in ``useOfficeStore``.
 */
export function OfficeSettingsCard() {
  const office = useOfficeStore((s) => s.office);
  const setOffice = useOfficeStore((s) => s.setOffice);
  const sprancaId = useOfficeStore((s) => s.sprancaId);
  const setSprancaId = useOfficeStore((s) => s.setSprancaId);
  const customSpranca = useOfficeStore((s) => s.customSpranca);

  const update = (patch: Partial<NonNullable<typeof office>>) =>
    setOffice({
      name_full: "",
      tagline: "",
      contact: "",
      ...office,
      ...patch,
    });

  return (
    <CollapsibleCard
      className="mt-6"
      icon={<Building2 className="h-4 w-4 text-ind-600" />}
      title="Ured — podaci za dokumente"
      summary={
        office?.name_full ? (
          <BadgeStatus kind="ok">{office.name_full}</BadgeStatus>
        ) : (
          <span className="text-stone-400">standardno zaglavlje</span>
        )
      }
    >
      <p className="mb-3 text-[0.8rem] text-stone-500">
        Vrijedi za <strong>sve projekte</strong>. Zaglavlje ide u podnožje, a
        podaci ureda i projektanta <strong>automatski popunjavaju izjave i
        imenovanja</strong> — ne moraš ih unositi po projektu. Pravni tekst
        izjava (zakoni, članci) ostaje standardan.
      </p>

      <div className="flex flex-col gap-3">
        <p className="text-[0.72rem] font-semibold uppercase tracking-wide text-stone-400">
          Špranca dokumentacije
        </p>
        <label className="flex flex-col gap-1">
          <span className="text-[0.78rem] font-medium text-stone-600">
            Izgled preuzetih dokumenata (zaglavlja, podnožja, numeracija)
          </span>
          <select
            className="h-9 rounded-md border border-stone-200 bg-white px-2 text-[0.85rem] text-stone-700"
            value={sprancaId ?? ""}
            onChange={(e) => setSprancaId(e.target.value || null)}
          >
            {sprancaOptions(customSpranca).map((o) => (
              <option key={o.id ?? "default"} value={o.id ?? ""}>
                {o.label}
              </option>
            ))}
          </select>
          <span className="text-[0.72rem] text-stone-400">
            „Špranca ureda" dodaje tablicu zaglavlja (građevina, faza, oznake…)
            na svaku stranicu PDF-a i Worda; „Permitko standard" je dosadašnji
            izgled.
          </span>
        </label>

        <SprancaUploadPanel />

        <p className="mt-2 text-[0.72rem] font-semibold uppercase tracking-wide text-stone-400">
          Zaglavlje (podnožje dokumenta)
        </p>
        <label className="flex flex-col gap-1">
          <span className="text-[0.78rem] font-medium text-stone-600">Naziv ureda</span>
          <Input
            value={office?.name_full ?? ""}
            onChange={(e) => update({ name_full: e.target.value })}
            placeholder="npr. STUDIO ARH d.o.o."
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[0.78rem] font-medium text-stone-600">Podnaslov</span>
          <Input
            value={office?.tagline ?? ""}
            onChange={(e) => update({ tagline: e.target.value })}
            placeholder="npr. Arhitektura & dizajn"
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[0.78rem] font-medium text-stone-600">
            Kontakt / adresa ureda
          </span>
          <Input
            value={office?.contact ?? ""}
            onChange={(e) => update({ contact: e.target.value })}
            placeholder="npr. Maršala Tita 100, 51410 Opatija, mob. 091 000 0000"
          />
        </label>

        <p className="mt-2 text-[0.72rem] font-semibold uppercase tracking-wide text-stone-400">
          Za izjave i imenovanja (tijelo dokumenta)
        </p>
        <label className="flex flex-col gap-1">
          <span className="text-[0.78rem] font-medium text-stone-600">
            Glavni projektant (ime i titula)
          </span>
          <Input
            value={office?.gp_name_full ?? ""}
            onChange={(e) => update({ gp_name_full: e.target.value })}
            placeholder="npr. Ana Anić, mag.ing.arh."
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[0.78rem] font-medium text-stone-600">
            Direktor ureda (potpisuje imenovanje projektanta)
          </span>
          <Input
            value={office?.direktor_name ?? ""}
            onChange={(e) => update({ direktor_name: e.target.value })}
            placeholder="npr. Ivan Ivić"
          />
        </label>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1">
            <span className="text-[0.78rem] font-medium text-stone-600">HKA klasa</span>
            <Input
              value={office?.hka_klasa ?? ""}
              onChange={(e) => update({ hka_klasa: e.target.value })}
              placeholder="UP/I-034-02/…"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[0.78rem] font-medium text-stone-600">HKA urbroj</span>
            <Input
              value={office?.hka_urbroj ?? ""}
              onChange={(e) => update({ hka_urbroj: e.target.value })}
              placeholder="505-04-…"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[0.78rem] font-medium text-stone-600">HKA broj</span>
            <Input
              value={office?.hka_broj ?? ""}
              onChange={(e) => update({ hka_broj: e.target.value })}
              placeholder="npr. 1234"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[0.78rem] font-medium text-stone-600">HKA datum</span>
            <Input
              value={office?.hka_date ?? ""}
              onChange={(e) => update({ hka_date: e.target.value })}
              placeholder="npr. 1. ožujka 2024."
            />
          </label>
        </div>
        <label className="flex flex-col gap-1">
          <span className="text-[0.78rem] font-medium text-stone-600">
            Adresa ureda za tijelo izjave{" "}
            <span className="font-normal text-stone-400">
              (nije obavezno — koristi kontakt gore)
            </span>
          </span>
          <Input
            value={office?.ured_addr ?? ""}
            onChange={(e) => update({ ured_addr: e.target.value })}
            placeholder="npr. J. P. Kamova 11a, Rijeka"
          />
        </label>
      </div>
    </CollapsibleCard>
  );
}
